import type { ReactNode } from 'react'
import { Logo } from '../../components/Logo'
import { AppleMark, GoogleMark } from '../../components/brands'

/**
 * Het frame om elk aanmeldscherm uit ronde 21 (21A t/m 21C, 21G t/m 21I).
 *
 * Aanmelden is Confetti zelf en niet het feest van iemand anders, dus dit is de
 * enige hoek van de app in het merkpalet: ink, koraal, oker. Zodra je binnen
 * bent gaat alles weer in de kleur van het event.
 *
 * Op desktop staat er een donkere kolom van 620px naast met wat de app doet
 * (21C). Die is puur tekst: een screenshot zou hier verouderen zonder dat
 * iemand het merkt.
 */
export function AuthShell({
  children,
  onBack,
}: {
  children: ReactNode

  /** Het pijltje linksboven uit ronde 21G. */
  onBack?: () => void
}) {
  return (
    <main className="bg-brand-cream text-ink font-brand-serif min-h-full lg:flex">
      <aside className="bg-brand-ink text-brand-on-ink hidden flex-col justify-between p-12 lg:flex lg:w-[620px] lg:shrink-0">
        <Logo size={30} to="/beheer/aanmelden" />

        <div>
          <p className="max-w-[440px] text-[46px] leading-[1.12] text-balance">
            Jullie gasten zien meer dan de fotograaf.
          </p>
          <p className="font-brand text-brand-ondark mt-4.5 max-w-[420px] text-[17px] leading-[1.55] text-pretty">
            Eén QR-code op tafel. Geen app, geen account voor je gasten.
          </p>
        </div>

        <div aria-hidden="true" className="flex gap-2.5">
          <span className="bg-brand-coral h-2.5 w-16 rounded-full" />
          <span className="bg-brand-ocher h-2.5 w-10 rounded-full" />
          <span className="h-2.5 w-6 rounded-full bg-[#3a3330]" />
        </div>
      </aside>

      <div className="flex min-h-full flex-1 flex-col px-7 pt-7 pb-10 lg:items-center lg:justify-center lg:p-12">
        <div className="w-full lg:max-w-[400px]">
          <div className="mb-8 flex items-center gap-3.5 lg:mb-0">
            {onBack && (
              <button
                type="button"
                aria-label="Terug"
                onClick={onBack}
                className="border-brand-line text-brand-soft hover:bg-brand-line/40 flex size-9.5 shrink-0 items-center justify-center rounded-full border text-base transition-colors"
              >
                ←
              </button>
            )}

            {/* Op desktop staat het merk al in de donkere kolom. */}
            <span className="lg:hidden">
              <Logo size={26} to="/beheer/aanmelden" />
            </span>
          </div>

          {children}
        </div>
      </div>
    </main>
  )
}

/** De kop boven elk formulier: titel in de serif, één zin eronder in de sans. */
export function AuthHeading({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <>
      <h1 className="text-[36px] leading-[1.1] lg:text-[40px]">{title}</h1>
      {children && (
        <p className="font-brand text-brand-soft mt-2.5 text-[15px] leading-[1.5] text-pretty lg:text-base">
          {children}
        </p>
      )}
    </>
  )
}

export function AuthField({
  id,
  label,
  action,
  children,
}: {
  id: string
  label: string

  /** Rechts naast het label, zoals "Vergeten?" bij het wachtwoord (21A). */
  action?: ReactNode
  children: ReactNode
}) {
  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between">
        <label htmlFor={id} className="text-brand-label text-[11px] tracking-[0.16em] uppercase">
          {label}
        </label>
        {action}
      </div>
      {children}
    </div>
  )
}

/** De velden onder elkaar, met de tussenruimte uit het ontwerp. */
export function AuthFields({ children }: { children: ReactNode }) {
  return <div className="font-brand mt-7.5 flex flex-col gap-4">{children}</div>
}

export const authInputClass =
  'w-full rounded-[14px] border border-brand-line bg-surface px-4 py-3.75 text-base outline-none ' +
  'placeholder:text-brand-faint focus:border-ink transition-colors lg:px-4.5 lg:py-4'

export function AuthSubmit({
  children,
  busy,
  disabled,
}: {
  children: ReactNode
  busy?: boolean
  disabled?: boolean
}) {
  return (
    <button
      type="submit"
      disabled={disabled || busy}
      className="bg-brand-coral text-brand-coral-ink font-brand disabled:bg-inert disabled:text-inert-ink mt-1 h-14 w-full rounded-full text-[17px] font-semibold shadow-[0_10px_24px_rgba(224,96,60,0.3)] transition hover:brightness-95 disabled:shadow-none disabled:hover:brightness-100"
    >
      {busy ? 'Even geduld…' : children}
    </button>
  )
}

export function AuthError({ children }: { children: ReactNode }) {
  return (
    <p
      role="alert"
      className="border-brand-coral/35 bg-brand-coral/10 text-brand-coral font-brand mt-4 rounded-[12px] border px-4 py-3 text-sm leading-[1.5]"
    >
      {children}
    </p>
  )
}

/**
 * "of" met een streep aan weerszijden, en daaronder Google en Apple.
 *
 * Die twee doen niets: er is geen enkele koppeling. Ze staan er wel, want ze
 * horen bij het ontwerp en weglaten zou de plek waar ze komen onzichtbaar
 * maken. Dat ze nog niet kunnen staat er nu ook, in een zin eronder — het stond
 * eerder alleen in een `title`, en die verschijnt niet op een telefoon.
 *
 * De merken zijn getekend en niet getypt. Het appeltje was een  uit Apples
 * eigen lettertype: op een Mac een appel, op Windows en Android een leeg
 * blokje. En een losse "G" is geen Google — de kleuren zijn juist het teken.
 */
export function AuthSocial() {
  return (
    <>
      <div className="my-3.5 flex items-center gap-3.5">
        <span className="bg-brand-line h-px flex-1" />
        <span className="text-brand-faint font-brand text-[11px] tracking-[0.18em] uppercase">
          of
        </span>
        <span className="bg-brand-line h-px flex-1" />
      </div>

      {/* Naast elkaar en niet onder elkaar: het zijn geen twee stappen maar
          twee keuzes, en zo blijft het formulier erboven de hoofdweg. */}
      <div className="grid grid-cols-2 gap-2.5">
        <SocialButton label="Google" mark={<GoogleMark className="size-[18px]" />} />
        <SocialButton label="Apple" mark={<AppleMark className="mb-px size-[19px]" />} />
      </div>

      <p className="text-brand-faint font-brand mt-3.5 text-center text-[13px] leading-[1.45]">
        Aanmelden met Google of Apple kan binnenkort.
      </p>
    </>
  )
}

/**
 * Uitgeschakeld, maar niet uitgewassen.
 *
 * Er stond `opacity-55` overheen, en dat haalde de knop én zijn tekst onder
 * elke leesbare drempel — een grijze vlek waarvan je niet meer ziet wát er niet
 * kan. Nu blijft de vorm scherp en zegt de regel eronder wat er aan de hand is.
 */
function SocialButton({ label, mark }: { label: string; mark: ReactNode }) {
  return (
    <button
      type="button"
      disabled
      className="border-brand-line font-brand text-brand-soft bg-surface flex h-13 items-center justify-center gap-2.5 rounded-full border text-[15px] font-medium"
    >
      {mark}
      {label}
    </button>
  )
}

/** "Nog geen account? Registreren" — de regel onderaan elk scherm. */
export function AuthSwitch({ question, children }: { question: string; children: ReactNode }) {
  return (
    <p className="font-brand text-brand-soft mt-7 text-center text-[15px]">
      {question} {children}
    </p>
  )
}

export const authLinkClass = 'text-brand-coral hover:underline'
